import { css, Global } from "@emotion/react"
import styled from "@emotion/styled"
import { useConsole } from "contexts/Console"
import getMediaQuery from "css/breakpoints"
import { animate, useMotionValue, useMotionValueEvent, useTransform } from "framer-motion"
import { createContext, useContext, useLayoutEffect, useRef, useState } from "react"
import { Helmet } from "react-helmet-async"
import { useLocation } from "react-router-dom"
import exposeEventTarget from "utils/exposeEventTarget"

const Context = createContext({})
const useNav = () => useContext(Context)

const cssVars = {
  main: {
    alert: {
      expanded: "--nav-main-alert-expanded",
      height: "--nav-main-alert-height",
    },
    bar: {
      height: "--nav-main-bar-height",
      pinned: "--nav-main-bar-pinned",
    },
    lock: "--nav-main-lock",
    panes: {
      expanded: "--nav-main-panes-expanded",
      primary: {
        expanded: "--nav-main-pane-primary-expanded",
        sub: {
          expanded: "--nav-main-pane-primary-sub-expanded",
          languages: {
            expanded: "--nav-main-pane-primary-sub-languages-expanded",
          },
        },
      },
      secondary: {
        expanded: "--nav-main-pane-secondary-expanded",
      },
    },
    safe: "--nav-main-safe-height",
    nsafe: "--nav-main-nsafe-height",
  },
  overlay: {
    opaque: "--nav-overlay-opaque",
  },
  sub: {
    lock: "--nav-sub-lock",
    pane: {
      expanded: "--nav-sub-pane-expanded",
    },
    toggle: {
      pinned: "--nav-sub-toggle-pinned",
      forceUnpinned: "--nav-sub-toggle-force-unpinned",
    },
  },
}

const globals = css`
  :root {
    ${cssVars.main.alert.expanded}: 0;
    ${cssVars.main.alert.height}: 6.875rem;
    ${getMediaQuery("m", { max: false })} {
      ${cssVars.main.alert.height}: 5.5rem;
    }

    ${cssVars.main.bar.height}: clamp(3.75rem, calc(3.352rem + 1.634vw), 5.313rem);
    ${cssVars.main.bar.pinned}: 1;
    ${cssVars.main.safe}: calc(var(${cssVars.main.bar.height}) + var(${cssVars.main.alert.height}) * round(up, var(${cssVars.main.alert.expanded})));

    ${cssVars.main.lock}: -1;

    ${cssVars.main.panes.expanded}: 0; /* any pane expanded */
    ${cssVars.main.panes.primary.expanded}: 0;
    ${cssVars.main.panes.primary.sub.expanded}: 0;
    ${cssVars.main.panes.primary.sub.languages.expanded}: 0;
    ${cssVars.main.panes.secondary.expanded}: 0;

    ${cssVars.overlay.opaque}: 0;

    ${cssVars.sub.lock}: -1;
    ${cssVars.sub.toggle.pinned}: 0;
    ${cssVars.sub.toggle.forceUnpinned}: 0;

    ${cssVars.sub.pane.expanded}: 0;
  }
`

let mainUnlockTimer
let mainLockQueue = Promise.resolve()
let subUnlockTimer
let subLockQueue = Promise.resolve()

const Wrapper = props => {
  const console = useConsole()
  const location = useLocation()
  const [et] = useState(new EventTarget())

  const computeSafeHeight = () => {
    const safe = getComputedStyle(document.body).getPropertyValue(cssVars.main.nsafe)
    MainSafe.set(safe)
  }

  const computeScrollLock = () => {
    const lock = Math.min(...[MainLock.get(), SubLock.get(), 1 - overlayOpaque.get()].filter(latest => latest >= 0))

    if (lock < 1 && ScrollLock.get() !== "hidden") {
      ScrollLock.set("hidden")
    } else if (lock === 1 && ScrollLock.get() !== "auto") {
      ScrollLock.set("auto")
    }
  }

  const ScrollLock = useMotionValue("auto")
  useMotionValueEvent(ScrollLock, "change", latest => {
    if (latest === "hidden") document.documentElement.style.setProperty("overflow", "hidden")
    else document.documentElement.style.removeProperty("overflow")
  })

  const MainLock = useMotionValue(-1)
  useMotionValueEvent(MainLock, "change", latest => {
    document.documentElement.style.setProperty(cssVars.main.lock, latest)
    computeScrollLock()
  })

  const MainAlertExpanded = useMotionValue(0)
  useMotionValueEvent(MainAlertExpanded, "change", value => {
    document.documentElement.style.setProperty(cssVars.main.alert.expanded, value)
    computeSafeHeight()
  })

  const MainSafe = useMotionValue(0)
  useLayoutEffect(() => {
    const controller = new AbortController()

    global.addEventListener("resize", computeSafeHeight, { signal: controller.signal })
    computeSafeHeight()
    if (!MainSafe.get()) requestAnimationFrame(computeSafeHeight)

    return () => controller.abort()
  })

  const MainPinned = useMotionValue(1) // header bar 1 sticky 0 hidden
  useMotionValueEvent(MainPinned, "change", value => {
    document.documentElement.style.setProperty(cssVars.main.bar.pinned, value)
  })

  const MainPrimaryPaneExpanded = useMotionValue(0) // main menu panel 1 shown 0 hidden
  useMotionValueEvent(MainPrimaryPaneExpanded, "change", value => {
    document.documentElement.style.setProperty(cssVars.main.panes.primary.expanded, value)
  })

  const MainPrimarySubPaneExpanded = useMotionValue(0) // main menu panel 1 shown 0 hidden
  useMotionValueEvent(MainPrimarySubPaneExpanded, "change", value => {
    document.documentElement.style.setProperty(cssVars.main.panes.primary.sub.expanded, value)
  })

  const MainPrimarySubLanguagesPaneExpanded = useMotionValue(0) // main menu panel 1 shown 0 hidden
  useMotionValueEvent(MainPrimarySubLanguagesPaneExpanded, "change", value => {
    document.documentElement.style.setProperty(cssVars.main.panes.primary.sub.languages.expanded, value)
  })

  const MainSecondaryPaneExpanded = useMotionValue(0) // secondary menu panel 1 shown 0 hidden
  useMotionValueEvent(MainSecondaryPaneExpanded, "change", value => {
    document.documentElement.style.setProperty(cssVars.main.panes.secondary.expanded, value)
  })

  const MainPanesExpanded = useTransform(() => {
    return Math.max(MainPrimaryPaneExpanded.get(), MainSecondaryPaneExpanded.get())
  })
  useMotionValueEvent(MainPanesExpanded, "change", latest => {
    document.documentElement.style.setProperty(cssVars.main.panes.expanded, latest)
  })

  const SubLock = useMotionValue(-1)
  useMotionValueEvent(SubLock, "change", latest => {
    document.documentElement.style.setProperty(cssVars.sub.lock, latest)
    computeScrollLock()
  })

  const SubToggleInstalled = useMotionValue(0)
  const SubTogglePinned = useTransform(MainPinned, [0, 1], [1, 0]) // sub nav launcher 1 sticky 0 hidden
  useMotionValueEvent(SubTogglePinned, "change", value => {
    document.documentElement.style.setProperty(cssVars.sub.toggle.pinned, value)
  })

  const SubToggleForceUnpinned = useMotionValue(0) // sub nav force pin state 1 force hide 0 normal
  useMotionValueEvent(SubToggleForceUnpinned, "change", value => {
    document.documentElement.style.setProperty(cssVars.sub.toggle.forceUnpinned, value)
  })

  const SubPaneExpanded = useMotionValue(0) // sub nav panel 1 shown 0 hidden
  useMotionValueEvent(SubPaneExpanded, "change", latest => {
    document.documentElement.style.setProperty(cssVars.sub.pane.expanded, latest)
  })

  const SubContentDisplay = useMotionValue(0)

  const overlayOpaque = useTransform(() => {
    return Math.max(MainPrimaryPaneExpanded.get(), MainSecondaryPaneExpanded.get(), SubPaneExpanded.get())
  })
  useMotionValueEvent(overlayOpaque, "change", latest => {
    document.documentElement.style.setProperty(cssVars.overlay.opaque, latest)
    computeScrollLock()
  })

  const collapseAll = () => {
    et.dispatchEvent(new Event("collapse:all"))
    return Promise.all(
      [
        animate(MainPrimaryPaneExpanded, 0),
        animate(MainPrimarySubPaneExpanded, 0),
        animate(MainPrimarySubLanguagesPaneExpanded, 0),
        animate(MainSecondaryPaneExpanded, 0),
        SubPaneExpanded.get() > 0 ? [animate(SubContentDisplay, 0), animate(SubPaneExpanded, 0, { delay: 0.2 })] : animate(SubPaneExpanded, 0),
      ].flat(Math.Infinity)
    )
  }

  useLayoutEffect(
    function reset() {
      ScrollLock.set("auto")
      MainLock.set(-1)
      MainPinned.set(1)
      MainPrimaryPaneExpanded.set(0)
      MainPrimarySubPaneExpanded.set(0)
      MainPrimarySubLanguagesPaneExpanded.set(0)
      MainSecondaryPaneExpanded.set(0)
      SubLock.set(-1)
      SubPaneExpanded.set(0)
      SubContentDisplay.set(0)
      SubToggleForceUnpinned.set(0)
      SubToggleInstalled.set(0)
    },
    [location]
  )

  const ctx = {
    __internals: {
      dispatchEvent: (...args) => et.dispatchEvent(...args),
    },
    ...exposeEventTarget(et),
    collapseAll,
    main: {
      alert: {
        motion: {
          expanded: MainAlertExpanded,
        },
      },
      bar: {
        motion: {
          pinned: MainPinned,
        },
      },
      lock: {
        motion: {
          lock: MainLock,
        },
        request: t => {
          const to = Math.round(Math.max(0, Math.min(1, t)))

          let resolver
          const release = new Promise(resolve => {
            resolver = resolve
          })
          mainLockQueue = mainLockQueue
            .then(() => {
              cancelAnimationFrame(mainUnlockTimer)

              const from = MainPinned.get()
              MainLock.set(from)

              if (from !== to) {
                animate(MainLock, to)
              }
              const event = new Event("main:lock")
              et.dispatchEvent(event)
              return [from, to]
            })
            .then(([from, to]) => Promise.all([Promise.resolve([from, to]), release]))
            .then(([[from, to]]) => {
              mainUnlockTimer = requestAnimationFrame(() => {
                const animation = from !== to ? animate(MainLock, from) : Promise.resolve()

                animation.then(() => {
                  MainLock.set(-1)
                  const event = new Event("main:unlock")
                  et.dispatchEvent(event)
                })
              })
            })

          return resolver
        },
      },
      panes: {
        motion: {
          expanded: MainPanesExpanded,
        },
        primary: {
          sub: {
            motion: {
              expanded: MainPrimarySubPaneExpanded,
            },
            languages: {
              motion: {
                expanded: MainPrimarySubLanguagesPaneExpanded,
              },
            },
          },
          motion: {
            expanded: MainPrimaryPaneExpanded,
          },
        },
        secondary: {
          motion: {
            expanded: MainSecondaryPaneExpanded,
          },
        },
      },
      safe: {
        motion: {
          height: MainSafe,
        },
      },
    },
    overlay: {
      motion: {
        opaque: overlayOpaque,
      },
    },
    sub: {
      lock: {
        motion: {
          lock: MainLock,
        },
        request: t => {
          const to = Math.round(Math.max(0, Math.min(1, t)))

          let resolver
          const release = new Promise(resolve => {
            resolver = resolve
          })
          subLockQueue = subLockQueue
            .then(() => {
              cancelAnimationFrame(subUnlockTimer)

              const from = SubTogglePinned.get()
              SubLock.set(from)

              if (from !== to) {
                animate(SubLock, to)
              }
              const event = new Event("sub:lock")
              et.dispatchEvent(event)
              return [from, to]
            })
            .then(([from, to]) => Promise.all([Promise.resolve([from, to]), release]))
            .then(([[from, to]]) => {
              subUnlockTimer = requestAnimationFrame(() => {
                const animation = from !== to ? animate(SubLock, from) : Promise.resolve()

                animation.then(() => {
                  SubLock.set(-1)
                  const event = new Event("sub:unlock")
                  et.dispatchEvent(event)
                })
              })
            })

          return resolver
        },
      },
      toggle: {
        motion: {
          pinned: SubTogglePinned,
          forceUnpinned: SubToggleForceUnpinned,
          installed: SubToggleInstalled,
        },
      },
      pane: {
        motion: {
          expanded: SubPaneExpanded,
        },
        content: {
          motion: {
            display: SubContentDisplay,
          },
        },
      },
    },
  }

  if (process.browser) {
    window.__navCTX = ctx
  }
  console.verbose("Nav:Wrapper(%o)", props)
  return (
    <>
      <Helmet>
        <style>{`
			@property ${cssVars.main.safe} {
				syntax: "<length>";
				inherits: true;
				initial-value: 0;
			}
			@property ${cssVars.main.nsafe} {
				syntax: "<number>";
				inherits: true;
				initial-value: 0;
			}


		:where(*) {
			${cssVars.main.nsafe}: tan(atan2(var(${cssVars.main.safe}), 1px));
		}
		`}</style>
      </Helmet>
      <Global styles={globals} />
      <Context.Provider value={ctx}>{props.children}</Context.Provider>
    </>
  )
}

export default Wrapper
export { cssVars, useNav }
